import React, { useEffect, useRef, useState } from "react";
import Decimal from "decimal.js";
import { PaymentHistoryView } from "./PaymentHistory";
import FillHistory from "./FillHistory";
import {
  Button,
  Card,
  Grid,
  Metric,
  Notice,
  Table,
  TableScroll,
  Row,
  View,
  DialogBackdrop,
  DialogPanel,
} from "./style";
import { AffiliatePersonalFees, portalTime, affiliateError } from "./fees";
import {
  AffiliateDashboardData,
  PaymentHistoryViewState,
  DashboardPresentationContext,
} from "./types";
import { AffiliateStatus } from "./lifecycle";
import {
  DashboardHeader,
  DashboardSummary,
  DashboardKpis,
  DashboardTabs,
  CountTag,
  ReferralHeader,
  LinkCards,
  DesktopOnly,
} from "./HandoffDashboard.style";
import { ReferredTradersView, PointsBudgetsView } from "./HandoffPointsTraders";
import HandoffBanner from "./HandoffBanner";

export type {
  AffiliateDashboardData,
  AffiliatePaymentsPage,
  PaymentHistoryViewState,
} from "./types";

interface PresentationContext extends DashboardPresentationContext {
  affiliateFillsError?: unknown;
  affiliateFillsLoading?: boolean;
  loadMoreAffiliateFills?: () => void;
  referralLink?: (code: string) => string;
}
export interface AffiliateDashboardViewProps {
  data: AffiliateDashboardData;
  payments: PaymentHistoryViewState;
  fillsLoading?: boolean;
  fillsError?: unknown;
  onLoadMoreFills?: () => void;
  name?: string;
  now?: string;
  accountName?: (address: string) => string;
  referralLink?: (code: string) => string;
}

function ReadOnlyDialog({
  title,
  onClose,
  children,
}: React.PropsWithChildren<{ title: string; onClose: () => void }>) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Tab") {
        const targets = Array.from(
          panel.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]),a[href],[tabindex="0"]'
          ) || []
        );
        const first = targets[0];
        const last = targets[targets.length - 1];
        if (
          event.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === panel.current)
        ) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.removeEventListener("keydown", keydown);
      previous?.focus();
    };
  }, [onClose]);
  return (
    <DialogBackdrop
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <DialogPanel
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className="dialog-header">
          <h2>{title}</h2>
          <button type="button" aria-label="Close dialog" onClick={onClose}>
            ×
          </button>
        </div>
        {children}
      </DialogPanel>
    </DialogBackdrop>
  );
}
function PayoutCard({
  payout,
  data,
}: {
  payout: AffiliateDashboardData["payout_preview"];
  data: AffiliateDashboardData;
}) {
  const cashPointsOnly = data.reward_policy === "cash_points_v1";
  const showDao =
    !cashPointsOnly ||
    Number(data.balances.dao_value_usd) > 0 ||
    Number(payout?.dao_eligible_usd) > 0 ||
    Number(payout?.dao_held_usd) > 0;
  return (
    <Card>
      <h2>Payout preview</h2>
      <p>
        Cash becomes eligible after a seven-day holdback. Cash payouts follow
        the program’s settlement schedule; this preview does not confirm a
        transfer.
      </p>
      <Notice>
        {cashPointsOnly
          ? "This program earns USDC commission and points. New DAO AEVO rewards and AEVO payouts are disabled."
          : "DAO AEVO rewards remain unpaid. The entitlement and any token estimate are displayed for review; AEVO delivery is not enabled."}
        {cashPointsOnly &&
          showDao &&
          " Historical DAO entitlements remain recorded and unpaid."}
      </Notice>
      {payout ? (
        <>
          <Grid>
            <Metric>
              <small>Eligible cash</small>
              <strong>${payout.cash_eligible_usd}</strong>
            </Metric>
            <Metric>
              <small>Held cash</small>
              <strong>${payout.cash_held_usd}</strong>
            </Metric>
            {showDao && (
              <>
                <Metric>
                  <small>
                    {cashPointsOnly
                      ? "Historical eligible DAO entitlement"
                      : "Eligible DAO entitlement"}
                  </small>
                  <strong>${payout.dao_eligible_usd}</strong>
                </Metric>
                <Metric>
                  <small>Held DAO entitlement</small>
                  <strong>${payout.dao_held_usd}</strong>
                </Metric>
                <Metric>
                  <small>AEVO quantity estimate</small>
                  <strong>
                    {payout.price_resolved
                      ? payout.dao_tokens
                      : "Price pending"}
                  </strong>
                </Metric>
              </>
            )}
          </Grid>
          {payout.decision_needed?.length ? (
            <Notice>
              Pending payout decisions: {payout.decision_needed.join(", ")}
            </Notice>
          ) : null}
        </>
      ) : (
        <p>Payout preview is not available.</p>
      )}
    </Card>
  );
}

type ApprovedDashboard = AffiliateDashboardData & {
  affiliate: NonNullable<AffiliateDashboardData["affiliate"]>;
};

function displayNumber(value: string, decimals = 2): string {
  try {
    const [whole, fraction] = new Decimal(value || "0")
      .toFixed(decimals)
      .split(".");
    return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${
      fraction === undefined ? "" : `.${fraction}`
    }`;
  } catch {
    return "—";
  }
}

function compactDate(value: string): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
        timeZone: "UTC",
      }).format(date)
    : "—";
}

function CopyGlyph() {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
    >
      <path
        d="M13.33 6h-6C6.6 6 6 6.6 6 7.33v6C6 14.07 6.6 14.67 7.33 14.67h6c.74 0 1.34-.6 1.34-1.34v-6C14.67 6.6 14.07 6 13.33 6Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.33 10h-.66c-.74 0-1.34-.6-1.34-1.33v-6c0-.74.6-1.34 1.34-1.34h6c.73 0 1.33.6 1.33 1.34v.66"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AffiliateSummary({
  data,
  now,
  name,
  onPayouts,
  onPersonalFees,
}: {
  data: ApprovedDashboard;
  now?: string;
  name?: string;
  onPayouts: () => void;
  onPersonalFees?: () => void;
}) {
  const { balances } = data;
  return (
    <DashboardSummary>
      <DashboardHeader>
        <div className="identity">
          <h2>{name || "Affiliate account"}</h2>
          <span className="tier">{data.affiliate.tier}</span>
        </div>
        {onPersonalFees && (
          <button
            type="button"
            className="personal-fees"
            onClick={onPersonalFees}
          >
            Your personal fees
          </button>
        )}
      </DashboardHeader>
      <span className="wallet-identity">{data.affiliate.address}</span>
      <AffiliateStatus affiliate={data.affiliate} now={now} />
      <DashboardKpis>
        <div>
          <small>Cash eligible for payout</small>
          <div className="value-row">
            <strong>${displayNumber(balances.cash_eligible_usd)}</strong>
            <button type="button" onClick={onPayouts}>
              View payouts
            </button>
          </div>
        </div>
        <div>
          <small>Cash in holdback</small>
          <div className="value-row">
            <strong>${displayNumber(balances.cash_held_usd)}</strong>
            <span className="holdback-detail">
              <span className="holdback">Seven-day hold</span>
              <small>until eligible</small>
            </span>
          </div>
        </div>
        <div>
          <small>Available points</small>
          <div className="value-row">
            <strong>{displayNumber(balances.available_points, 0)}</strong>
            <Button
              disabled
              title="Read-only: points must be allocated from the affiliate wallet"
            >
              Spend
            </Button>
          </div>
        </div>
      </DashboardKpis>
      {Number(balances.frozen_points || "0") > 0 && (
        <p>Frozen points: {displayNumber(balances.frozen_points || "0", 0)}</p>
      )}
    </DashboardSummary>
  );
}

function HistoryPagination({
  api,
  data,
}: {
  api: PresentationContext;
  data: AffiliateDashboardData;
}) {
  let olderTradesLabel = "Load older trades";
  if (api.affiliateFillsLoading) olderTradesLabel = "Loading older trades…";
  else if (api.affiliateFillsError) olderTradesLabel = "Retry older trades";
  return (
    <Row>
      {data.history_totals && (
        <small>
          {data.fills.length} of {data.history_totals.fill_count} recorded fills
          loaded
        </small>
      )}
      {api.affiliateFillsError ? (
        <Notice $error role="alert">
          {affiliateError(api.affiliateFillsError)}
        </Notice>
      ) : null}
      {data.fills_next_cursor && api.loadMoreAffiliateFills && (
        <Button
          $secondary
          disabled={api.affiliateFillsLoading}
          onClick={() => api.loadMoreAffiliateFills?.()}
        >
          {olderTradesLabel}
        </Button>
      )}
    </Row>
  );
}

type DashboardForm = "personal";

function referralUrl(api: PresentationContext, code: string) {
  return (
    api.referralLink?.(code) ||
    `https://app.aevo.xyz/affiliates?code=${encodeURIComponent(code)}`
  );
}

function DashboardContents({
  api,
  data,
  paymentHistory,
}: {
  api: PresentationContext;
  data: ApprovedDashboard;
  paymentHistory?: React.ReactNode;
}) {
  const active = !data.affiliate.terminated_at;
  const pricingEnabled = true;
  const [section, setSection] = useState<
    "links" | "traders" | "points" | "payouts"
  >("links");
  const [form, setForm] = useState<DashboardForm>();
  const [copyStatus, setCopyStatus] = useState("");
  const accountName = api.accountLabel?.(data.affiliate.address);
  const codeAvailable = (code: (typeof data.codes)[number]) =>
    active &&
    !code.revoked_at &&
    Date.parse(code.expires_at) > portalTime(api.now) &&
    (!code.use_cap || code.uses < code.use_cap);
  const activeCodes = data.codes.filter(codeAvailable).length;
  const openForm = (next: DashboardForm) => {
    setForm(next);
  };
  const copyLink = async (code: string) => {
    try {
      await navigator.clipboard.writeText(referralUrl(api, code));
      setCopyStatus(`Referral link for ${code} copied.`);
    } catch {
      setCopyStatus(`Could not copy. Referral link: ${referralUrl(api, code)}`);
    }
  };
  const tabs: { key: typeof section; label: string }[] = [
    { key: "links", label: "Referral links" },
    { key: "traders", label: "Referred traders" },
    { key: "points", label: "Points & zero fees" },
    { key: "payouts", label: "Payouts" },
  ];

  return (
    <>
      <AffiliateSummary
        data={data}
        now={api.now}
        name={
          accountName && !/^0x/i.test(accountName) ? accountName : undefined
        }
        onPayouts={() => setSection("payouts")}
        onPersonalFees={() => openForm("personal")}
      />
      <DashboardTabs role="tablist" aria-label="Affiliate dashboard sections">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={section === tab.key}
            onClick={() => setSection(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </DashboardTabs>
      {section === "links" && (
        <Card role="tabpanel">
          <ReferralHeader>
            <div className="title">
              <h2>Your referral links</h2>
              <CountTag>{activeCodes} active</CountTag>
            </div>
            {pricingEnabled && (
              <Button
                disabled
                title="Read-only: create a code from the affiliate wallet"
              >
                Create code
              </Button>
            )}
          </ReferralHeader>
          {data.codes.length ? (
            <>
              <DesktopOnly>
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th className="spacer" aria-hidden="true" />
                        <th>Uses</th>
                        <th className="spacer" aria-hidden="true" />
                        <th>Expires</th>
                        <th className="spacer" aria-hidden="true" />
                        <th>Perps T / M</th>
                        <th className="spacer" aria-hidden="true" />
                        <th>Options T / M</th>
                        <th className="spacer" aria-hidden="true" />
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.codes.map((code) => (
                        <tr key={code.code}>
                          <td>
                            <strong>{code.code}</strong>
                            {!codeAvailable(code) && (
                              <small>
                                {code.revoked_at
                                  ? " · Deleted"
                                  : " · Unavailable"}
                              </small>
                            )}
                          </td>
                          <td className="spacer" aria-hidden="true" />
                          <td>
                            {code.uses} / {code.use_cap || "Unlimited"}
                          </td>
                          <td className="spacer" aria-hidden="true" />
                          <td>{compactDate(code.expires_at)}</td>
                          <td className="spacer" aria-hidden="true" />
                          <td>
                            {code.rates.perp_taker_bps} /{" "}
                            {code.rates.perp_maker_bps} bps
                          </td>
                          <td className="spacer" aria-hidden="true" />
                          <td>
                            {code.rates.option_taker_bps} /{" "}
                            {code.rates.option_maker_bps} bps
                          </td>
                          <td className="spacer" aria-hidden="true" />
                          <td>
                            <Row>
                              {pricingEnabled && !code.revoked_at && (
                                <Button
                                  $secondary
                                  disabled
                                  title="Read-only: edit a code from the affiliate wallet"
                                >
                                  Edit
                                </Button>
                              )}
                              <Button
                                disabled
                                title="Read-only: share cards are available from the affiliate wallet; use Copy link to inspect the referral URL"
                              >
                                Share
                              </Button>
                              <Button
                                $secondary
                                className="copy-button"
                                aria-label="Copy link"
                                title="Copy link"
                                disabled={!codeAvailable(code)}
                                onClick={() => copyLink(code.code)}
                              >
                                <CopyGlyph />
                              </Button>
                            </Row>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
              </DesktopOnly>
              <LinkCards>
                {data.codes.map((code) => (
                  <article key={code.code}>
                    <div className="top">
                      <strong>
                        {code.code}
                        {!codeAvailable(code) &&
                          (code.revoked_at ? " · Deleted" : " · Unavailable")}
                      </strong>
                      <CountTag>
                        {code.uses} / {code.use_cap || "Unlimited"}
                      </CountTag>
                    </div>
                    <div className="stats">
                      <div>
                        <small>Perps T/M</small>
                        <span>
                          {code.rates.perp_taker_bps} /{" "}
                          {code.rates.perp_maker_bps} bps
                        </span>
                      </div>
                      <div>
                        <small>Options T/M</small>
                        <span>
                          {code.rates.option_taker_bps} /{" "}
                          {code.rates.option_maker_bps} bps
                        </span>
                      </div>
                      <div>
                        <small>Expires</small>
                        <span>{compactDate(code.expires_at)}</span>
                      </div>
                    </div>
                    <div className="actions">
                      <Button
                        disabled
                        title="Read-only: share cards are available from the affiliate wallet; use Copy link to inspect the referral URL"
                      >
                        Share
                      </Button>
                      {pricingEnabled && !code.revoked_at && (
                        <Button
                          $secondary
                          disabled
                          title="Read-only: edit a code from the affiliate wallet"
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        $secondary
                        className="copy-button"
                        aria-label="Copy link"
                        disabled={!codeAvailable(code)}
                        onClick={() => copyLink(code.code)}
                      >
                        <CopyGlyph />
                      </Button>
                    </div>
                  </article>
                ))}
              </LinkCards>
            </>
          ) : (
            <p>
              No referral links yet. Create a code to start inviting traders.
            </p>
          )}
          {!active && (
            <Notice>
              These codes are inactive and cannot accept new traders.
            </Notice>
          )}
        </Card>
      )}
      {section === "traders" && (
        <div role="tabpanel">
          <ReferredTradersView data={data} api={api} />
        </div>
      )}
      {section === "points" && (
        <div role="tabpanel">
          <PointsBudgetsView data={data} api={api} />
        </div>
      )}
      {section === "payouts" && (
        <div role="tabpanel">
          {paymentHistory}
          <Card as="details">
            <summary>Payout details</summary>
            <details>
              <summary>Cash payout preview</summary>
              <PayoutCard payout={data.payout_preview} data={data} />
            </details>
            <details>
              <summary>Trading earnings</summary>
              <FillHistory
                rewardPolicy={data.reward_policy}
                fills={data.fills}
                accountLabel={(wallet) =>
                  data.wallets
                    .find(
                      (trader) =>
                        trader.wallet.toLowerCase() === wallet.toLowerCase()
                    )
                    ?.nickname?.trim() ||
                  api.accountLabel?.(wallet) ||
                  wallet
                }
                limit={data.fills.length}
              />
              <HistoryPagination api={api} data={data} />
            </details>
            <details>
              <summary>Need help with a payout?</summary>
              <Row>
                <a
                  href="https://docs.aevo.xyz/help-and-support/tickets"
                  target="_blank"
                  rel="noreferrer"
                >
                  Submit a ticket
                </a>
                <a
                  href="https://discord.com/invite/aevo"
                  target="_blank"
                  rel="noreferrer"
                >
                  Join Discord
                </a>
              </Row>
            </details>
          </Card>
        </div>
      )}
      {form === "personal" && (
        <ReadOnlyDialog
          title="Your personal fees"
          onClose={() => setForm(undefined)}
        >
          {data.owner_floor_applied === true &&
          data.owner_fee_policy === "tier_floor_v1" &&
          data.owner_floor_activated_at ? (
            <AffiliatePersonalFees
              floors={data.floors}
              tier={data.affiliate.tier}
            />
          ) : (
            <Notice>
              Personal tier-floor pricing is not active for this account. Its
              status updates automatically.
            </Notice>
          )}
        </ReadOnlyDialog>
      )}
      {copyStatus && <p role="status">{copyStatus}</p>}
    </>
  );
}

export function AffiliateDashboardView({
  data,
  payments,
  fillsLoading,
  fillsError,
  onLoadMoreFills,
  name,
  now,
  accountName,
  referralLink,
}: AffiliateDashboardViewProps) {
  const context: PresentationContext = {
    now,
    accountLabel: (address) =>
      (address.toLowerCase() === data.affiliate?.address.toLowerCase() &&
        name) ||
      accountName?.(address) ||
      address,
    affiliateFillsLoading: fillsLoading,
    affiliateFillsError: fillsError,
    loadMoreAffiliateFills: onLoadMoreFills,
    referralLink,
  };
  return (
    <View aria-label="Affiliate dashboard read-only viewer">
      <div className="dashboard-content">
        <span className="read-only-label">
          Read-only administrator view · actions that change this account are
          disabled
        </span>
        <HandoffBanner />
        {!data.enabled && (
          <Notice role="status">
            The affiliate program is currently inactive for this account.
          </Notice>
        )}
        {data.affiliate ? (
          <DashboardContents
            key={data.affiliate.address.toLowerCase()}
            data={data as ApprovedDashboard}
            api={context}
            paymentHistory={<PaymentHistoryView history={payments} />}
          />
        ) : (
          <Card>
            <h2>Affiliate approval required</h2>
            <p>This account is not an approved affiliate.</p>
          </Card>
        )}
      </div>
    </View>
  );
}
export default AffiliateDashboardView;
